import { formatDuration } from 'date-fns'
import { getCourse } from 'libs/server/api'

export type Course = Awaited<ReturnType<typeof getCourse>>

// same as web/apps/next/src/pages/classes/[slug]/components/utils.js
// but with abbreviated format (hr mins)
export const getCourseTime = (
  chapters: ReturnType<typeof getChaptersInfo>['chapters'],
  subchaptersCount: number,
) => {
  let totalTimeSeconds = 0

  if (subchaptersCount) {
    totalTimeSeconds = chapters.reduce((acum, chapter) => {
      if (
        !chapter.duration ||
        !('startTime' in chapter) ||
        !('endTime' in chapter)
      )
        return acum
      // For subchapters, duration is endTime - startTime
      return acum + (chapter.endTime - chapter.startTime)
    }, 0)
  } else {
    // Loop through each chapter duration
    for (let i = chapters.length - 1; i >= 0; i -= 1) {
      if (!chapters[i].duration) break

      const durationSplit = String(chapters[i].duration).split(':')
      let hours = '0'
      if (durationSplit.length === 3) {
        hours = durationSplit.shift() || '0'
      }
      const [minutes, seconds] = durationSplit
      totalTimeSeconds += parseInt(hours, 10) * 60 * 60
      totalTimeSeconds += parseInt(minutes, 10) * 60
      totalTimeSeconds += parseInt(seconds, 10)
    }
  }

  const duration = {
    hours: Math.floor(totalTimeSeconds / 60 / 60),
    minutes: Math.floor(totalTimeSeconds / 60) % 60,
  }

  // Custom locale for abbreviated format
  const customLocale = {
    formatDistance: (token: string, count: number) => {
      const formatDistanceLocale: Record<string, string> = {
        xHours: count === 1 ? '1hr' : `${count}hr`,
        xMinutes: count === 1 ? '1mins' : `${count}mins`,
      }
      return formatDistanceLocale[token] || ''
    },
  }

  return formatDuration(duration, {
    format: ['hours', 'minutes'],
    locale: customLocale,
  })
}

export const getChaptersInfo = (course: Course) => {
  const subchaptersCount = course.sortedSubchapters?.length
  const chapters = subchaptersCount
    ? course.sortedSubchapters
    : course.sortedChapters

  return {
    chapters,
    chaptersCount: chapters.length,
    subchaptersCount,
  }
}

export const getChaptersAndDuration = (course: Course) => {
  const { chapters, chaptersCount, subchaptersCount } = getChaptersInfo(course)
  return {
    chaptersCount,
    courseDuration: getCourseTime(chapters, subchaptersCount),
  }
}
